const TRANSITION_DURATION = 400
const CLOSE_FADE_DELAY = 250
const LOCK_BADGE_CYCLE_INTERVAL = 4000
const LOCK_BADGE_REVEAL_DURATION = 500

class DecadeShowcase extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.onTriggerClick = this.onTriggerClick.bind(this)
    this.onTriggerKeydown = this.onTriggerKeydown.bind(this)
    this.onCloseClick = this.onCloseClick.bind(this)
    this.onCardClick = this.onCardClick.bind(this)
    this.onHeightTransitionEnd = this.onHeightTransitionEnd.bind(this)
    this.handleResize = this.handleResize.bind(this)

    if (this.els.trigger.exists) {
      this.listeners.add(
        this.els.trigger.element,
        'click',
        this.onTriggerClick,
      )
      this.listeners.add(
        this.els.trigger.element,
        'keydown',
        this.onTriggerKeydown,
      )
    }

    if (this.els.close.exists) {
      this.listeners.add(this.els.close.element, 'click', this.onCloseClick)
    }

    this.listeners.add(this, 'click', this.onCardClick)
    this.listeners.add(window, 'resize', this.handleResize)

    this.fadingContentPlaceholders = new Map()

    this.updateTriggerA11y()
    this.setupLockBadgeCycle()
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    window.clearInterval(this.lockBadgeInterval)
  }

  handleResize () {
    this.updateTriggerA11y()

    if (!this.isMobile()) this.resetToDesktop()
  }

  resetToDesktop () {
    window.clearTimeout(this.closingTimeout)
    window.clearTimeout(this.collapseTimeout)

    this.style.removeProperty('transition')
    this.style.removeProperty('block-size')
    this.style.removeProperty('max-block-size')

    this.unfreezeFadingContent()

    if (!this.isAlwaysExpanded()) {
      this.classList.remove('util-Expanded')
    }

    if (this.els.trigger.exists) {
      this.els.trigger.element.setAttribute(
        'aria-expanded',
        String(this.isAlwaysExpanded()),
      )
    }
  }

  updateTriggerA11y () {
    if (!this.els.trigger.exists) return
    this.els.trigger.element.setAttribute(
      'tabindex',
      this.isMobile() ? '0' : '-1',
    )
  }

  onTriggerKeydown (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    this.onTriggerClick()
  }

  onTriggerClick (event) {
    if (this.isAlwaysExpanded()) return
    if (!this.isMobile()) return
    if (this.classList.contains('util-Expanded')) return

    event?.stopPropagation()
    this.setExpanded(true)
  }

  onCloseClick (event) {
    if (this.isAlwaysExpanded()) return

    event.stopPropagation()
    this.setExpanded(false)
  }

  onCardClick (event) {
    if (this.isAlwaysExpanded()) return
    if (!this.isMobile()) return
    if (!this.classList.contains('util-Expanded')) return
    if (event.target.closest('a, button')) return
    this.setExpanded(false)
  }

  setExpanded (expanded) {
    if (this.classList.contains('util-Expanded') === expanded) return

    this.style.removeProperty('transition')
    this.style.removeProperty('block-size')
    this.style.removeProperty('max-block-size')

    window.clearTimeout(this.closingTimeout)
    window.clearTimeout(this.collapseTimeout)

    const startHeight = this.getBoundingClientRect().height

    if (!expanded) this.freezeFadingContent()

    this.classList.toggle('util-Expanded', expanded)

    if (expanded) {
      this.unfreezeFadingContent()
    } else {
      this.closingTimeout = window.setTimeout(() => {
        this.unfreezeFadingContent()
      }, CLOSE_FADE_DELAY + TRANSITION_DURATION)
    }

    if (this.els.trigger.exists) {
      this.els.trigger.element.setAttribute('aria-expanded', String(expanded))
    }

    const endHeight = this.getBoundingClientRect().height

    if (startHeight === endHeight) return

    this.style.transition = 'none'
    this.style.maxBlockSize = 'none'
    this.style.blockSize = `${startHeight}px`

    this.getBoundingClientRect()

    const startHeightTransition = () => {
      this.style.transition = `block-size ${TRANSITION_DURATION}ms ease`
      this.style.blockSize = `${endHeight}px`

      this.listeners.add(this, 'transitionend', this.onHeightTransitionEnd, {
        once: true,
      })
    }

    if (expanded) {
      startHeightTransition()
    } else {
      this.collapseTimeout = window.setTimeout(
        startHeightTransition,
        CLOSE_FADE_DELAY,
      )
    }
  }

  freezeFadingContent () {
    const cardRect = this.getBoundingClientRect()

    this.fadingContentElements().forEach((element) => {
      const rect = element.getBoundingClientRect()

      const placeholder = document.createElement('div')
      placeholder.setAttribute('aria-hidden', 'true')
      placeholder.style.inlineSize = `${rect.width}px`
      placeholder.style.blockSize = `${rect.height}px`

      element.before(placeholder)
      this.fadingContentPlaceholders.set(element, placeholder)

      element.style.position = 'absolute'
      element.style.zIndex = '2'
      element.style.insetBlockStart = `${rect.top - cardRect.top}px`
      element.style.insetInlineStart = `${rect.left - cardRect.left}px`
      element.style.inlineSize = `${rect.width}px`

      this.appendChild(element)
    })
  }

  unfreezeFadingContent () {
    this.fadingContentElements().forEach((element) => {
      element.style.removeProperty('position')
      element.style.removeProperty('z-index')
      element.style.removeProperty('inset-block-start')
      element.style.removeProperty('inset-inline-start')
      element.style.removeProperty('inline-size')

      const placeholder = this.fadingContentPlaceholders.get(element)
      if (placeholder) {
        placeholder.replaceWith(element)
        this.fadingContentPlaceholders.delete(element)
      }
    })
  }

  fadingContentElements () {
    return [this.els.contentlogo, this.els.heading, this.els.details]
      .filter((el) => el?.exists)
      .map((el) => el.element)
  }

  onHeightTransitionEnd (event) {
    if (event.target !== this) return
    this.style.removeProperty('block-size')
    this.style.removeProperty('transition')
    this.style.removeProperty('max-block-size')
  }

  setupLockBadgeCycle () {
    if (!this?.els?.lockedtext?.exists) return

    const element = this.els.lockedtext.element
    const droppingDate = new Date(element.dataset.droppingDatetime)

    if (Number.isNaN(droppingDate.getTime()) || droppingDate <= new Date()) {
      return
    }

    this.lockBadgeLabel = element.textContent
    this.lockBadgeShowingCountdown = false

    this.lockBadgeInterval = window.setInterval(() => {
      if (new Date() >= droppingDate) {
        window.clearInterval(this.lockBadgeInterval)
        this.revealLockBadgeText(element, this.lockBadgeLabel)
        return
      }

      this.lockBadgeShowingCountdown = !this.lockBadgeShowingCountdown

      this.revealLockBadgeText(
        element,
        this.lockBadgeShowingCountdown
          ? this.formatDroppingText(element, droppingDate)
          : this.lockBadgeLabel,
      )
    }, LOCK_BADGE_CYCLE_INTERVAL)
  }

  revealLockBadgeText (element, text) {
    element.style.transition = 'none'
    element.style.clipPath = 'inset(0 100% 0 0)'
    element.textContent = text

    element.getBoundingClientRect()

    element.style.transition = `clip-path ${LOCK_BADGE_REVEAL_DURATION}ms ease`
    element.style.clipPath = 'inset(0 0 0 0)'
  }

  formatDroppingText (element, droppingDate) {
    const { differenceInDays, differenceInHours, addDays } = window.dateFns
    const now = new Date()
    const days = differenceInDays(droppingDate, now)
    const hours = differenceInHours(droppingDate, addDays(now, days))

    return `${element.dataset.droppingLabel} : ${days} ${element.dataset.daysLabel} : ${hours} ${element.dataset.hoursLabel}`
  }

  isMobile () {
    return window.BAO.MediaBreakpoints.isMd === false
  }

  isAlwaysExpanded () {
    return this.dataset.alwaysExpanded === 'true'
  }
}

if (!customElements.get('decade-showcase')) {
  customElements.define('decade-showcase', DecadeShowcase)
}
